/**
 * Security section with 2FA and password management
 */

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { SetPasswordDialog } from '@/components/integrations/SetPasswordDialog';
import { Badge } from '@/lib/base/badges/badges';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { fetchWithDefaults } from '@/lib/http/client';

interface SecuritySectionProps {
  orgId: number;
  has2FA: boolean;
  hasPassword: boolean;
  onRefresh?: () => void;
}

export function SecuritySection({
  orgId: _orgId,
  has2FA,
  hasPassword,
  onRefresh,
}: SecuritySectionProps) {
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const openPasswordDialog = useCallback(
    () => setIsPasswordDialogOpen(true),
    []
  );
  const closePasswordDialog = useCallback(
    () => setIsPasswordDialogOpen(false),
    []
  );
  const dismissError = useCallback(() => setError(null), []);
  const dismissSuccess = useCallback(() => setSuccess(null), []);

  const handleDisable2FARequest = useCallback(() => {
    setIsDisableConfirmOpen(true);
  }, []);

  const handleConfirmDisable2FA = useCallback(async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      const response = await fetchWithDefaults('/users/api/totp-disable/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        setSuccess('Two-factor authentication has been disabled.');
        if (onRefresh) {
          try {
            await onRefresh();
          } catch (refreshErr) {
            console.error(
              'Error refreshing profile after 2FA disable:',
              refreshErr
            );
          }
        }
        setIsDisableConfirmOpen(false);
      } else {
        const errorData = await response.json();
        setError(
          errorData.error || "We couldn't disable two-factor authentication"
        );
        throw new Error(errorData.error || 'Failed to disable 2FA');
      }
    } catch (err) {
      console.error('Error disabling 2FA:', err);
      // Use functional update to avoid overwriting specific server error
      // already set in the else branch above
      setError(prev => prev || "We couldn't disable two-factor authentication");
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [onRefresh]);

  const handleCancelDisable2FA = useCallback(() => {
    setIsDisableConfirmOpen(false);
  }, []);

  return (
    <div className='space-y-6'>
      {error && (
        <AlertFloating
          color='error'
          title={error}
          description=''
          onClose={dismissError}
        />
      )}

      {success && (
        <AlertFloating
          color='success'
          title={success}
          description=''
          onClose={dismissSuccess}
        />
      )}

      <ButtonList aria-label='Security settings'>
        {/* Two-Factor Authentication */}
        <ButtonList.Item
          trailing={
            has2FA ? (
              <Button
                onClick={handleDisable2FARequest}
                isDisabled={actionLoading}
                isLoading={actionLoading}
                color='secondary-destructive'
                size='sm'
              >
                Disable 2FA
              </Button>
            ) : (
              <Link to='/account/authenticators/totp'>
                <Button color='primary' size='sm'>
                  Enable 2FA
                </Button>
              </Link>
            )
          }
        >
          <div className='flex items-center gap-3'>
            <p className='font-medium text-primary'>
              Two-Factor Authentication
            </p>
            {has2FA && (
              <Badge type='color' size='sm' color='success'>
                Enabled
              </Badge>
            )}
          </div>
          <p className='mt-0.5 text-sm text-tertiary'>
            Add an extra layer of security to your account
          </p>
        </ButtonList.Item>

        {/* Password */}
        <ButtonList.Item
          trailing={
            <Button color='secondary' size='sm' onClick={openPasswordDialog}>
              {hasPassword ? 'Change Password' : 'Set Password'}
            </Button>
          }
        >
          <p className='font-medium text-primary'>Password</p>
          <p className='mt-0.5 text-sm text-tertiary'>
            {hasPassword
              ? 'Change your password'
              : 'Set a password for your account'}
          </p>
        </ButtonList.Item>
      </ButtonList>

      <ConfirmationDialog
        isOpen={isDisableConfirmOpen}
        title='Disable two-factor authentication?'
        description='This will make your account less secure. Are you sure you want to disable 2FA?'
        variant='destructive'
        confirmLabel='Disable 2FA'
        isLoading={actionLoading}
        onConfirm={handleConfirmDisable2FA}
        onCancel={handleCancelDisable2FA}
      />

      <SetPasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={closePasswordDialog}
      />
    </div>
  );
}
